import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// As duas navegações alternativas às abas (preferência `navegacaoLista`).
// Quem decide o que existe — quais abas, se Resolvidos é uma delas — é o
// TicketsManager; aqui é só o desenho.

// A contagem chega como a lista a mede: número ou piso ("40+").
const numero = (contagem) => parseInt(contagem, 10) || 0;
const textoDaContagem = (contagem) =>
  numero(contagem) > 99 ? "99+" : contagem;

// Um item do trilho. Estreito (lista de ~340px) só o ícone com a contagem em
// bolinha, como as abas; com largura, nome e número por extenso. A medida é a
// da coluna da lista (`@container` em pages/Tickets), não a da janela.
const ItemDoTrilho = ({
  icone: Icone,
  rotulo,
  ajuda,
  contagem,
  ativo,
  // Pasta pessoal: liga e desliga um filtro, não troca de aba.
  alternar = false,
  // Prazo de pausa vencido: a mesma cobrança que o botão da barra faz.
  alerta = false,
  onClick,
}) => (
  <button
    type="button"
    title={ajuda || rotulo}
    aria-label={numero(contagem) > 0 ? `${rotulo} (${contagem})` : rotulo}
    aria-pressed={alternar ? ativo : undefined}
    aria-current={!alternar && ativo ? "true" : undefined}
    onClick={onClick}
    className={cn(
      "flex w-full items-center justify-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors @[30rem]:justify-start",
      ativo
        ? "bg-background font-semibold text-foreground shadow-sm ring-1 ring-border"
        : "text-muted-foreground hover:bg-background hover:text-foreground",
    )}
  >
    <span className="relative shrink-0">
      <Icone className="h-4 w-4" />
      {numero(contagem) > 0 && (
        <span
          className={cn(
            "absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums text-primary-foreground @[30rem]:hidden",
            alerta ? "animate-pulse bg-rose-600" : "bg-primary",
          )}
        >
          {textoDaContagem(contagem)}
        </span>
      )}
    </span>
    <span className="hidden min-w-0 flex-1 truncate @[30rem]:inline">
      {rotulo}
    </span>
    {numero(contagem) > 0 && (
      <span
        className={cn(
          "hidden text-xs font-medium tabular-nums @[30rem]:inline",
          alerta && "animate-pulse text-rose-600 dark:text-rose-400",
        )}
      >
        {textoDaContagem(contagem)}
      </span>
    )}
  </button>
);

const Legenda = ({ children }) => (
  <span className="hidden px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground @[30rem]:block">
    {children}
  </span>
);

// Coluna de pastas à esquerda da lista. Fica por cima do recuo que o
// TicketsManager abre (pl-12 / pl-40) — as larguras andam juntas.
export const TrilhoDaLista = ({ itens, atual, contagens, aoIr, pastas }) => (
  <nav
    aria-label="Pastas de atendimentos"
    className="scrollbar-discreta absolute inset-y-0 left-0 z-10 flex w-12 flex-col gap-0.5 overflow-y-auto border-r bg-muted/40 px-1.5 py-3 @[30rem]:w-40 @[30rem]:px-2"
  >
    <Legenda>Atendimentos</Legenda>
    {itens.map((item) => (
      <ItemDoTrilho
        key={item.value}
        icone={item.icone}
        rotulo={item.rotulo}
        ajuda={item.ajuda}
        contagem={contagens[item.value]}
        ativo={atual === item.value}
        onClick={() => aoIr(item.value)}
      />
    ))}

    <div className="mx-1 my-2 h-px shrink-0 bg-border" />

    <Legenda>Pessoais</Legenda>
    {pastas.map(({ value, ...pasta }) => (
      <ItemDoTrilho key={value} alternar {...pasta} />
    ))}
  </nav>
);

// Um botão só no lugar das abas: "Minhas · 4", com as outras no menu. A
// pílula "N aguardando" é o que as abas mostravam sem pedir — sem ela, quem
// fica em Minhas não veria o cliente esperando.
// `children`: a barra de ferramentas, quando é curta (fica à direita).
export const SeletorDaLista = ({ itens, atual, contagens, aoIr, children }) => {
  const Icone = atual.icone;
  const aguardando = numero(contagens.pending);

  return (
    <div className="flex shrink-0 items-center gap-2 px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title={atual.ajuda}
            className="flex h-9 min-w-0 items-center gap-1.5 rounded-md border px-2.5 text-sm font-semibold transition-colors hover:bg-muted"
          >
            <Icone className="h-4 w-4 shrink-0" />
            <span className="truncate">{atual.rotulo}</span>
            {numero(contagens[atual.value]) > 0 && (
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                · {contagens[atual.value]}
              </span>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {itens.map((item) => {
            const IconeDoItem = item.icone;

            return (
              // O atual vai em cor, não com ✓: o ✓ à direita se confundia com
              // o ícone de Resolvidos (✓✓) da mesma lista.
              <DropdownMenuItem
                key={item.value}
                title={item.ajuda}
                aria-current={atual.value === item.value || undefined}
                onSelect={() => aoIr(item.value)}
                className={cn(
                  "gap-2",
                  atual.value === item.value && "font-semibold text-primary",
                )}
              >
                <IconeDoItem className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{item.rotulo}</span>
                {numero(contagens[item.value]) > 0 && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {contagens[item.value]}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {atual.value !== "pending" && aguardando > 0 && (
        <button
          type="button"
          onClick={() => aoIr("pending")}
          className="whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-500/25 dark:text-amber-400"
        >
          {contagens.pending} aguardando
        </button>
      )}

      {children && <div className="ml-auto">{children}</div>}
    </div>
  );
};
